import { useEffect, useState } from "react";
import { Alert, Button, Empty, Input, List, message, Popconfirm, Space } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import usePrismSubscriptionNoteService from "../../services/Prism/subscriptionNoteService";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.errors?.body?.[0] ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const PrismSubscriptionNotes = ({ subscriptionId }) => {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const {
    getSubscriptionNotes,
    subscriptionNoteLoading,
    createSubscriptionNote,
    createSubscriptionNoteLoading,
    updateSubscriptionNote,
    updateSubscriptionNoteLoading,
    deleteSubscriptionNote,
    deleteSubscriptionNoteLoading,
  } = usePrismSubscriptionNoteService();

  const loadNotes = async () => {
    try {
      setLoadError(null);
      const response = await getSubscriptionNotes(subscriptionId);
      setNotes(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setLoadError(getErrorMessage(error, "Failed to load subscription notes."));
    }
  };

  useEffect(() => {
    setNotes([]);
    setBody("");
    setEditingNote(null);
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId]);

  const handleSubmit = async () => {
    const trimmedBody = body.trim();
    if (!trimmedBody) {
      message.warning("Please enter a note.");
      return;
    }

    try {
      if (editingNote) {
        await updateSubscriptionNote(subscriptionId, editingNote.id, {
          body: trimmedBody,
        });
        message.success("Subscription note updated successfully.");
      } else {
        await createSubscriptionNote(subscriptionId, { body: trimmedBody });
        message.success("Subscription note added successfully.");
      }
      setBody("");
      setEditingNote(null);
      await loadNotes();
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to save subscription note."));
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await deleteSubscriptionNote(subscriptionId, noteId);
      message.success("Subscription note deleted successfully.");
      if (editingNote?.id === noteId) {
        setEditingNote(null);
        setBody("");
      }
      await loadNotes();
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to delete subscription note."));
    }
  };

  const saving = createSubscriptionNoteLoading || updateSubscriptionNoteLoading;

  return (
    <section className="prism-subscription-details__section prism-subscription-notes">
      <h3>Subscription Notes</h3>
      {loadError && <Alert message={loadError} showIcon type="error" />}
      <div className="prism-subscription-notes__composer">
        <Input.TextArea
          maxLength={5000}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Add a note specific to this subscription"
          rows={3}
          showCount
          value={body}
        />
        <Space className="prism-subscription-notes__composer-actions">
          {editingNote && (
            <Button
              disabled={saving}
              onClick={() => {
                setEditingNote(null);
                setBody("");
              }}
            >
              Cancel Edit
            </Button>
          )}
          <Button loading={saving} onClick={handleSubmit} type="primary">
            {editingNote ? "Save Note" : "Add Note"}
          </Button>
        </Space>
      </div>
      <List
        dataSource={notes}
        loading={subscriptionNoteLoading}
        locale={{ emptyText: <Empty description="No subscription notes yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        renderItem={(note) => (
          <List.Item
            actions={
              note.is_owner
                ? [
                    <Button
                      aria-label="Edit subscription note"
                      icon={<EditOutlined />}
                      key="edit"
                      onClick={() => {
                        setEditingNote(note);
                        setBody(note.body);
                      }}
                      size="small"
                      type="text"
                    />,
                    <Popconfirm
                      key="delete"
                      title="Delete this subscription note?"
                      onConfirm={() => handleDelete(note.id)}
                    >
                      <Button
                        aria-label="Delete subscription note"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deleteSubscriptionNoteLoading}
                        size="small"
                        type="text"
                      />
                    </Popconfirm>,
                  ]
                : []
            }
          >
            <List.Item.Meta
              className="prism-subscription-notes__meta"
              description={formatTimestamp(note.created_at)}
              title={note.author_name}
            />
            <div className="prism-subscription-notes__body">{note.body}</div>
          </List.Item>
        )}
      />
    </section>
  );
};

export default PrismSubscriptionNotes;
